/*
 * The city landing pages (Decision 110): the only list of them. The routes, the page, the head tags, the prerender
 * (`scripts/prerender.mjs`), the sitemap (`scripts/generate-sitemap.mjs`, held equal by `sitemap.test.ts`), the home
 * destination tiles and the results canonical all read it. Adding a city here adds a page; its texts go in the
 * catalogues (`city.<slug>.*`), which `tsc` then requires in both languages. Each page also exists in English under
 * `/en/…` with the city's English slug (Decision 112).
 */
import { paths } from '@/app/paths';
import type { Locale } from '@/i18n';

export interface LandingCity {
  /** URL slug: `/location-voiture/<slug>`, `/activites/<slug>`. */
  slug: CitySlug;
  /** English URL slug: `/en/car-rental/<en>`, `/en/experiences/<en>`. */
  en: string;
  /** The city as the backend stores it (search `city`, agency city). */
  city: string;
  /** IATA code of the city's airport. */
  airport: string;
}

export type CitySlug = 'marrakech' | 'casablanca' | 'rabat' | 'tanger' | 'fes' | 'agadir';

/** Car rental pages, most searched first. */
export const CAR_CITIES: readonly LandingCity[] = [
  // i18n-ignore-start: backend city values (CarRent-dash AgencyForm: «Tangier», «Fes»)
  { slug: 'marrakech', en: 'marrakech', city: 'Marrakech', airport: 'RAK' },
  { slug: 'casablanca', en: 'casablanca', city: 'Casablanca', airport: 'CMN' },
  { slug: 'rabat', en: 'rabat', city: 'Rabat', airport: 'RBA' },
  { slug: 'tanger', en: 'tangier', city: 'Tangier', airport: 'TNG' },
  { slug: 'fes', en: 'fez', city: 'Fes', airport: 'FEZ' },
  { slug: 'agadir', en: 'agadir', city: 'Agadir', airport: 'AGA' },
  // i18n-ignore-end
];

/** Experience pages: only cities whose catalogue has experiences (Marrakech today). */
export const EXPERIENCE_CITIES: readonly LandingCity[] = CAR_CITIES.filter((c) => c.slug === 'marrakech');

/** The city of a page URL slug, in that page's language (`tanger` in French, `tangier` in English). */
export const carCity = (slug: string | undefined, lang: Locale = 'fr') =>
  CAR_CITIES.find((c) => (lang === 'en' ? c.en : c.slug) === slug);
export const experienceCity = (slug: string | undefined, lang: Locale = 'fr') =>
  EXPERIENCE_CITIES.find((c) => (lang === 'en' ? c.en : c.slug) === slug);

/** URL of a city's car page in a language. */
export const carCityPath = (c: LandingCity, lang: Locale) =>
  lang === 'en' ? paths.carCityEn(c.en) : paths.carCity(c.slug);
/** URL of a city's experiences page in a language. */
export const experienceCityPath = (c: LandingCity, lang: Locale) =>
  lang === 'en' ? paths.experienceCityEn(c.en) : paths.experienceCity(c.slug);

/** Both language versions of a landing page (`hreflang`), French first. */
export interface Alternates {
  fr: string;
  en: string;
}

/** The French version of an English landing URL (`/en/car-rental/tangier` → `/location-voiture/tanger`), if any. */
export function frenchPathOf(pathname: string): string | undefined {
  const [, en, kind, slug] = pathname.split('/');
  if (en !== 'en') return undefined;
  if (kind === 'car-rental') {
    const c = carCity(slug, 'en');
    return c && paths.carCity(c.slug);
  }
  if (kind === 'experiences') {
    const c = experienceCity(slug, 'en');
    return c && paths.experienceCity(c.slug);
  }
  return undefined;
}

const cityKey = (city: string) => city.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

/** Other spellings of a backend city («Tanger», «Fès», «Fez»). */
const ALIASES: Record<string, CitySlug> = { tanger: 'tanger', tangier: 'tanger', fes: 'fes', fez: 'fes' };

/** The car landing page of a search city («Marrakech», «Tangier», «Fès»…), if it has one. */
export function carCityOf(city: string): LandingCity | undefined {
  const key = cityKey(city.split(' – ')[0] ?? city);
  const slug = ALIASES[key] ?? key;
  return carCity(slug);
}
